import React, { useEffect, useState } from "react";
import { Button, Divider, Modal, Select, Text, TextInput } from "@mantine/core";
import { Search } from "tabler-icons-react";
import { showNotification } from "@mantine/notifications";
import AgreementListModal from "./AgreementListModal";
import { DatePicker } from "@mantine/dates";
import {
  differenceInMonths,
  differenceInYears,
  differenceInDays,
  addYears,
  addMonths,
  addDays,
  parseISO,
  subDays,
  formatISO,
} from "date-fns";
import { getBuList } from "../../utilities/apis/users";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { getAgreementCategoriesActive } from "../../utilities/apis/agreementCategories";
import { DEFAULT_AGREEMENT_TYPE_OPTIONS } from "../../components/AgreementTypeFilter";

export const INDIA_STATES_UNIONS = [
  { label: "Andhra Pradesh", value: "Andhra Pradesh" },
  { label: "Karnataka", value: "Karnataka" },
  {
    label: "Andaman and Nicobar Islands",
    value: "Andaman and Nicobar Islands",
  },
  { label: "Arunachal Pradesh", value: "Arunachal Pradesh" },
  { label: "Assam", value: "Assam" },
  { label: "Bihar", value: "Bihar" },
  { label: "Chandigarh", value: "Chandigarh" },
  { label: "Chattisgarh", value: "Chattisgarh" },
  { label: "Dadra and Nagar Haveli", value: "Dadra and Nagar Haveli" },
  { label: "Daman and Diu", value: "Daman and Diu" },
  { label: "Delhi", value: "Delhi" },
  { label: "Goa", value: "Goa" },
  { label: "Gujarat", value: "Gujarat" },
  { label: "Haryana", value: "Haryana" },
  { label: "Himachal Pradesh", value: "Himachal Pradesh" },
  { label: "Jammu and Kashmir", value: "Jammu and Kashmir" },
  { label: "Jharkhand", value: "Jharkhand" },
  { label: "Kerala", value: "Kerala" },
  { label: "Lakshadweep Islands", value: "Lakshadweep Islands" },
  { label: "Madhya Pradesh", value: "Madhya Pradesh" },
  { label: "Maharashtra", value: "Maharashtra" },
  { label: "Manipur", value: "Manipur" },
  { label: "Meghalaya", value: "Meghalaya" },
  { label: "Mizoram", value: "Mizoram" },
  { label: "Nagaland", value: "Nagaland" },
  { label: "Odisha", value: "Odisha" },
  { label: "Pondicherry", value: "Pondicherry" },
  { label: "Punjab", value: "Punjab" },
  { label: "Rajasthan", value: "Rajasthan" },
  { label: "Sikkim", value: "Sikkim" },
  { label: "Tamil Nadu", value: "Tamil Nadu" },
  { label: "Telangana", value: "Telangana" },
  { label: "Tripura", value: "Tripura" },
  { label: "Uttar Pradesh", value: "Uttar Pradesh" },
  { label: "Uttarakhand", value: "Uttarakhand" },
  { label: "West Bengal", value: "West Bengal" },
];

const LegacyAgreementForm1 = ({
  setLegacyAgreementFormValue,
  legacyAgreementFormValue,
  uiConfigs,
  setUiConfigs,
  saveAgreement,
}) => {
  const typeOfDocuments = [
    "Parent",
    "Supplementary",
    "Supplementary to Supplementary",
  ];
  const [errors, setErrors] = useState({});
  const [businessUnitsOptions, setBusinessUnitsOptions] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAgreementTypes = async () => {
    setLoading(true);
    try {
      const resp = await apiWrapWithErrorWithData(
        getAgreementCategoriesActive(),
      );
      if (Array.isArray(resp?.data)) {
        setOptions(
          resp.data.map((item) => ({
            label: item.name,
            value: item.name,
          })),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessUnits = async () => {
    try {
      const response = await apiWrapWithErrorWithData(getBuList());

      if (response.success) {
        const formattedData = response.result.map(({ id, name }) => ({
          value: id.toString(),
          label: name,
        }));
        setBusinessUnitsOptions(formattedData);
      }
    } catch (error) {
      console.error("Error fetching business units:", error);
    }
  };

  useEffect(() => {
    loadAgreementTypes();
    fetchBusinessUnits();
  }, []);
  const reminderLabels = {
    15: "15 days",
    30: "30 days",
    45: "45 days",
    60: "60 days",
  };
  // Improved saveNext function with detailed validation
  const saveNext = async () => {
    // 1. Initialize a clean error object
    const keys = {};

    // 2. Extract values for easier reading
    const {
      relationshipType,
      relationshipWith,
      typeOfAgreement,
      titleOfAgreement,
      dateOfContract,
      businessUnit,
      noOfYears,
      noOfMonths,
    } = legacyAgreementFormValue;

    // 3. Document Type Validation
    if (!relationshipType || relationshipType.trim().length < 3) {
      keys.relationshipType = "Type of document is mandatory.";
    }

    // 4. Connected Agreement Validation
    const isSupplementary =
      relationshipType === "Supplementary" ||
      relationshipType === "Supplementary to Supplementary";

    if (isSupplementary) {
      // Check if it's null, undefined, or an empty string after trimming
      if (!relationshipWith || relationshipWith.toString().trim() === "") {
        keys.relationshipWith =
          "Connected agreement number is required for supplementary documents.";
      }
    }

    // 5. Category and Title Validation
    if (!typeOfAgreement || !typeOfAgreement.trim()) {
      keys.typeOfAgreement = "agreement category is mandatory.";
    }

    // 6. Date Validation
    if (!dateOfContract || isNaN(parseISO(dateOfContract).getTime())) {
      keys.dateOfContract = "please enter a valid starting date.";
    }

    // 7. Business Unit Validation
    if (!businessUnit) {
      keys.businessUnit = "please select a Business Unit.";
    }

    // 8. Duration Validation (Ensure at least years or months is > 0)
    // const years = parseInt(noOfYears, 10) || 0;
    // const months = parseInt(noOfMonths, 10) || 0;

    // if (years <= 0 && months <= 0) {
    //   keys.noOfYears = "duration required.";
    //   keys.noOfMonths = "Check months.";
    // }
    const years = parseInt(legacyAgreementFormValue.noOfYears, 10) || 0;
    const months = parseInt(legacyAgreementFormValue.noOfMonths, 10) || 0;
    const days = parseInt(legacyAgreementFormValue.noOfDays, 10) || 0;

    const hasDuration = years > 0 || months > 0 || days > 0;
    const hasEndDate = Boolean(legacyAgreementFormValue.dueDateOfContract);

    // Either duration OR end date is mandatory
    if (!hasDuration && !hasEndDate) {
      keys.noOfYears = "duration or end date is required.";
      keys.dueDateOfContract = "duration or end date is required.";
    }

    // End date must be > starting date
    if (
      hasEndDate &&
      legacyAgreementFormValue.dateOfContract &&
      parseISO(legacyAgreementFormValue.dueDateOfContract) <=
        parseISO(legacyAgreementFormValue.dateOfContract)
    ) {
      keys.dueDateOfContract = "end date must be after the starting date.";
    }

    // 9. Execute based on error count

    if (Object.keys(keys).length > 0) {
      setErrors(keys);
      showNotification({
        color: "red",
        title: "agreement form",
        message: "please check all fields are filled properly.",
      });
    } else {
      setErrors({}); // Clear errors on success
      await saveAgreement();
      setUiConfigs({
        ...uiConfigs,
        currentStep: 1,
      });
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(0);
  const handleSearchClick = () => {
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
  };

  useEffect(() => {
    if (selectedAgreement) {
      setLegacyAgreementFormValue({
        ...legacyAgreementFormValue,
        relationshipWith: selectedAgreement + "",
      });
    }
  }, [selectedAgreement]);

  const resetDuration = () => {
    setLegacyAgreementFormValue({
      ...legacyAgreementFormValue,
      noOfYears: "0",
      noOfMonths: "0",
      noOfDays: "0",
      dueDateOfContract: null,
    });
  };

  const calculateEndDateFromDuration = (startISO, years, months, days) => {
    if (!startISO) return null;
    let date = parseISO(startISO);
    if (years) date = addYears(date, Number(years));
    if (months) date = addMonths(date, Number(months));
    if (days) date = addDays(date, Number(days));
    return formatISO(subDays(date, 1));
  };

  const calculateDurationFromDates = (startISO, endISO) => {
    const start = parseISO(startISO);
    const end = addDays(parseISO(endISO), 1);
    if (end <= start) return { years: "0", months: "0", days: "0" };
    const years = differenceInYears(end, start);
    const afterYears = addYears(start, years);
    const months = differenceInMonths(end, afterYears);
    const afterMonths = addMonths(afterYears, months);
    const days = differenceInDays(end, afterMonths);
    return {
      years: years.toString(),
      months: months.toString(),
      days: days.toString(),
    };
  };

  const handleDurationChange = (field, value) => {
    const nextValues = {
      ...legacyAgreementFormValue,
      [field]: value ?? "0",
    };

    if (nextValues.dateOfContract) {
      const years = Number(nextValues.noOfYears || 0);
      const months = Number(nextValues.noOfMonths || 0);
      const days = Number(nextValues.noOfDays || 0);

      const endISO = calculateEndDateFromDuration(
        nextValues.dateOfContract,
        years,
        months,
        days,
      );

      nextValues.dueDateOfContract = endISO;
    }

    setLegacyAgreementFormValue(nextValues);
  };

  return (
    <>
      {showModal && (
        <Modal
          overflow="inside"
          opened
          onClose={handleCloseModal} // Call the handler on modal close
          size="calc(80vw)"
          title="Select Agreement"
        >
          <AgreementListModal
            onCloseAgreement={handleCloseModal}
            setSelectedAgreement={setSelectedAgreement}
          />
        </Modal>
      )}
      <div className="w-full flex flex-col my-4">
        <div className="grid grid-cols-2">
          <div>
            <Select
              required
              className="max-w-lg"
              label="Type of document"
              placeholder="select type of document"
              data={typeOfDocuments.map((value) => ({ value, label: value }))}
              onChange={(selectedOption) => {
                setLegacyAgreementFormValue({
                  ...legacyAgreementFormValue,
                  relationshipType: selectedOption,
                });
              }}
              value={legacyAgreementFormValue.relationshipType}
              style={{ maxWidth: "82%" }}
              error={errors.relationshipType && errors.relationshipType}
            />
          </div>
          <div style={{ maxWidth: "82%" }} className="flex">
            <TextInput
              label="Connected agreement"
              placeholder="enter agreement number"
              required={
                legacyAgreementFormValue.relationshipType === "Supplementary" ||
                legacyAgreementFormValue.relationshipType ===
                  "Supplementary to Supplementary"
              }
              onChange={(e) => {
                setLegacyAgreementFormValue({
                  ...legacyAgreementFormValue,
                  relationshipWith: e.target.value,
                });
              }}
              value={legacyAgreementFormValue.relationshipWith}
              type="number"
              style={{
                width: "88%",
                display: "inline-block",
                marginRight: "2%",
              }}
              error={errors.relationshipWith && errors.relationshipWith}
            />
            <Button
              className="max-w-lg"
              color="#2196F3"
              onClick={handleSearchClick}
              style={{
                width: "10%",
                display: "inline-block",
                marginBottom: "0",
                marginTop: "auto",
                padding: "0",
                backgroundColor: "#46BDE1",
              }}
            >
              <Search size={20} strokeWidth={3} />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 mt-4">
          <Select
            searchable
            required
            creatable
            className="max-w-lg"
            style={{ maxWidth: "82%" }}
            label="Agreement category"
            placeholder="select agreement category"
            data={DEFAULT_AGREEMENT_TYPE_OPTIONS}
            onChange={(selectedOption) => {
              setLegacyAgreementFormValue({
                ...legacyAgreementFormValue,
                typeOfAgreement: selectedOption,
              });
            }}
            value={legacyAgreementFormValue.typeOfAgreement}
            error={errors.typeOfAgreement && errors.typeOfAgreement}
          />
          <TextInput
            label="Job order"
            placeholder="enter job order"
            value={legacyAgreementFormValue.jobOrder}
            onChange={(e) => {
              setLegacyAgreementFormValue({
                ...legacyAgreementFormValue,
                jobOrder: e.target.value,
              });
            }}
            error={errors.jobOrder && errors.jobOrder}
            style={{ maxWidth: "82%" }}
          />
        </div>

        <Divider className="mt-8" />
        <Text size="md" color="#46BDE1" className="my-4">
          term & location
        </Text>
        <div className="grid grid-cols-2 mt-2 mb-2">
          <DatePicker
            required
            style={{ maxWidth: "82%" }}
            label="Starting date"
            placeholder="select starting date"
            withinPortal
            dropdownPosition="bottom"
            onChange={(val) => {
              if (val && val.getTime) {
                const nextDate = formatISO(val);
                const nextValues = {
                  ...legacyAgreementFormValue,
                  dateOfContract: nextDate,
                };

                const years = Number(legacyAgreementFormValue.noOfYears || 0);
                const months = Number(legacyAgreementFormValue.noOfMonths || 0);
                const days = Number(legacyAgreementFormValue.noOfDays || 0);

                if (years > 0 || months > 0 || days > 0) {
                  nextValues.dueDateOfContract = calculateEndDateFromDuration(
                    nextDate,
                    years,
                    months,
                    days,
                  );
                } else if (legacyAgreementFormValue.dueDateOfContract) {
                  const {
                    years: y,
                    months: m,
                    days: d,
                  } = calculateDurationFromDates(
                    nextDate,
                    legacyAgreementFormValue.dueDateOfContract,
                  );
                  nextValues.noOfYears = String(y);
                  nextValues.noOfMonths = String(m);
                  nextValues.noOfDays = String(d);
                }
                setLegacyAgreementFormValue(nextValues);
              } else {
                setLegacyAgreementFormValue({
                  ...legacyAgreementFormValue,
                  dateOfContract: null,
                });
              }
            }}
            value={
              legacyAgreementFormValue.dateOfContract
                ? parseISO(legacyAgreementFormValue.dateOfContract)
                : null
            }
            error={errors.dateOfContract && errors.dateOfContract}
          />

          <div
            className="flex flex-row"
            style={{ marginBottom: "20px", maxWidth: "82%" }}
          >
            <Select
              label="Duration"
              required
              placeholder="years"
              data={[
                ...Array.from({ length: 11 }, (_, i) => ({
                  value: `${i}`,
                  label: `${i} years`,
                })),
                { value: "100", label: "100 years" },
              ]}
              value={legacyAgreementFormValue.noOfYears}
              onChange={(val) => handleDurationChange("noOfYears", val)}
              style={{ width: "160px", marginRight: "20px" }}
            />
            <Select
              label=" "
              placeholder="months"
              data={[
                ...Array.from({ length: 13 }, (_, i) => ({
                  value: `${i}`,
                  label: `${i} months`,
                })),
              ]}
              value={legacyAgreementFormValue.noOfMonths}
              onChange={(val) => handleDurationChange("noOfMonths", val)}
              style={{ width: "160px", marginRight: "20px" }}
            />
            <Select
              label=" "
              placeholder="days"
              data={[
                ...Array.from({ length: 32 }, (_, i) => ({
                  value: `${i}`,
                  label: `${i} days`,
                })),
              ]}
              value={legacyAgreementFormValue.noOfDays}
              onChange={(val) => handleDurationChange("noOfDays", val)}
              style={{ width: "160px" }}
            />
          </div>

          <DatePicker
            label="End date"
            required
            style={{ maxWidth: "82%", marginBottom: "20px" }}
            placeholder="select end date"
            withinPortal
            dropdownPosition="bottom"
            onChange={(val) => {
              if (!val || !legacyAgreementFormValue.dateOfContract) {
                resetDuration();
                return;
              }

              const endISO = formatISO(val);

              const { years, months, days } = calculateDurationFromDates(
                legacyAgreementFormValue.dateOfContract,
                endISO,
              );

              setLegacyAgreementFormValue((prev) => ({
                ...prev,
                dueDateOfContract: endISO,
                noOfYears: String(years),
                noOfMonths: String(months),
                noOfDays: String(days),
              }));
            }}
            value={
              legacyAgreementFormValue.dueDateOfContract
                ? parseISO(legacyAgreementFormValue.dueDateOfContract)
                : null
            }
            error={errors.dueDateOfContract}
          />

          <Select
            style={{ marginBottom: "20px", maxWidth: "82%" }}
            placeholder="reminder time"
            label="Renewal reminder time (in days)"
            data={[
              { value: 15, label: reminderLabels["15"] },
              { value: 30, label: reminderLabels["30"] },
              { value: 45, label: reminderLabels["45"] },
              { value: 60, label: reminderLabels["60"] },
            ]}
            onChange={(val) => {
              setLegacyAgreementFormValue({
                ...legacyAgreementFormValue,
                reminderTimeInDays: val,
              });
            }}
            value={legacyAgreementFormValue.reminderTimeInDays}
          />
          {/* <Select
            style={{ marginBottom: "20px", maxWidth: "82%" }}
            searchable
            placeholder="enter location"
            label="Location"
            data={[...INDIA_STATES_UNIONS]}
            onChange={(val) => {
              setLegacyAgreementFormValue({
                ...legacyAgreementFormValue,
                location: val,
              });
            }}
            value={legacyAgreementFormValue.location}
          /> */}
          <Select
            required
            searchable
            placeholder="enter business unit"
            label="Business unit"
            data={businessUnitsOptions}
            onChange={(val) => {
              setLegacyAgreementFormValue({
                ...legacyAgreementFormValue,
                businessUnit: val,
              });
            }}
            value={legacyAgreementFormValue.businessUnit}
            dropdownPosition="bottom"
            style={{ maxWidth: "82%" }}
            error={errors.businessUnit && errors.businessUnit}
          />
          {/* <TextInput required label="Location" placeholder='Enter location' {...legacyAgreementFormValue.getInputProps('location')}/> */}
        </div>
      </div>
      <div className="flex flex-row justify-between">
        <div></div>
        <Button
          className="max-w-xs right mt-20"
          style={{
            backgroundColor: "#46BDE1",
          }}
          onClick={saveNext}
        >
          next
        </Button>
      </div>
    </>
  );
};

export default LegacyAgreementForm1;
